import { useEffect, useRef, useCallback, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { COLOR_PALETTE, type PaletteColor } from '@the-wall/shared';
import { useCanvasStore } from '@/stores/canvasStore';
import { useAuthStore } from '@/stores/authStore';
import { useChatStore } from '@/stores/chatStore';
import { useUiStore } from '@/stores/uiStore';
import { placePixel } from '@/services/socket';
import { showSnackbar } from '@/stores/snackbarStore';
import { getCursorPositionFromWheelEvent, getWheelZoomDelta } from '@/utils/canvasZoom';
import { SEO } from '@/components/SEO';
import { useOpenWelcomeModal } from '@/contexts/WelcomeModalContext';
import { ZoomControls } from './ZoomControls';
import { PixelInfo } from './PixelInfo';
import { AdminPanel } from './AdminPanel';
import { OverlayPanel } from './OverlayPanel';
import { LiveUsersModal } from '@/components/LiveUsersModal';
import { ExpansionHistoryModal } from '@/components/ExpansionHistoryModal';

const PIXEL_SIZE = 1;
const DRAG_THRESHOLD = 5; // pixels moved before considered a drag
const MIN_VIEWPORT_FILL = 0.6; // Canvas should fill a portion of viewport

interface CanvasPixel {
  x: number;
  y: number;
}

type InteractionMode = 'inspect' | 'paint';

// Calculate zoom to fill at least (MIN_VIEWPORT_FILL * 100)% of viewport
function calculateMinZoom(
  containerWidth: number,
  containerHeight: number,
  canvasPixelWidth: number,
  canvasPixelHeight: number
): number {
  const canvasWidth = canvasPixelWidth * PIXEL_SIZE;
  const canvasHeight = canvasPixelHeight * PIXEL_SIZE;

  // Calculate zoom needed to fill (MIN_VIEWPORT_FILL * 100)% of each dimension
  const zoomForWidth = (containerWidth * MIN_VIEWPORT_FILL) / canvasWidth;
  const zoomForHeight = (containerHeight * MIN_VIEWPORT_FILL) / canvasHeight;

  // Use the smaller one so it fits in both dimensions
  return Math.min(zoomForWidth, zoomForHeight);
}

export function Canvas() {
  const navigate = useNavigate();
  const openWelcomeModal = useOpenWelcomeModal();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const paletteScrollMobileRef = useRef<HTMLDivElement>(null);
  const paletteScrollDesktopRef = useRef<HTMLDivElement>(null);

  // Drag state
  const isDragging = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dragStartPan = useRef({ x: 0, y: 0 });
  const hasDragged = useRef(false);

  // Touch/pinch state
  const lastPinchDistance = useRef<number | null>(null);
  const lastPinchZoom = useRef<number>(1);
  const pinchCenter = useRef({ x: 0, y: 0 });
  const pinchStartPan = useRef({ x: 0, y: 0 });

  const [interactionMode, setInteractionMode] = useState<InteractionMode>('inspect');

  // Focus state for keyboard navigation
  const [canvasFocused, setCanvasFocused] = useState(false);

  // Live users modal (clicking Live badge when connected)
  const [liveModalOpen, setLiveModalOpen] = useState(false);
  const [expansionModalOpen, setExpansionModalOpen] = useState(false);

  const {
    pixels,
    width,
    height,
    activePixels,
    totalPixels,
    completionRatio,
    completionThreshold,
    expansionCount,
    expandByPixels,
    selectedColor,
    zoom,
    panX,
    panY,
    hoveredPixel,
    inspectedPixel,
    isConnected,
    yoloMode,
    overlayPixels,
    overlayBounds,
    overlayVisible,
    overlayOpacity,
    expansionCelebration,
    setZoomAtPoint,
    setZoomAndPan,
    setMinZoom,
    setViewportSize,
    setPan,
    setHoveredPixel,
    setInspectedPixel,
    setPixel,
    setSelectedColor,
    clearExpansionCelebration,
  } = useCanvasStore();

  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.adminRole === 'ADMIN' || user?.adminRole === 'SUPER_ADMIN';
  const hasUnreadChat = useChatStore((state) => state.hasUnread);
  const openChat = useUiStore((state) => state.setChatOpen);
  const openLeaderboard = useUiStore((state) => state.setLeaderboardOpen);

  const setInspectMode = useCallback(() => {
    setInteractionMode('inspect');
  }, []);

  const setPaintMode = useCallback(() => {
    setInteractionMode('paint');
    setInspectedPixel(null);
  }, [setInspectedPixel]);

  // Calculate and set minimum zoom to fill (MIN_VIEWPORT_FILL * 100)% of viewport, and track viewport size
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateViewport = () => {
      const { width: containerWidth, height: containerHeight } = container.getBoundingClientRect();
      const minZoom = calculateMinZoom(containerWidth, containerHeight, width, height);
      setMinZoom(minZoom);
      setViewportSize(containerWidth, containerHeight);
    };

    // Set initial values
    updateViewport();

    // Update on resize
    const resizeObserver = new ResizeObserver(updateViewport);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [width, height, setMinZoom, setViewportSize]);

  // Render canvas
  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    // Clear canvas
    ctx.fillStyle = '#2a2a2a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw pixels
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const color = pixels[y]?.[x];
        if (color) {
          ctx.fillStyle = color;
          ctx.fillRect(x * PIXEL_SIZE, y * PIXEL_SIZE, PIXEL_SIZE, PIXEL_SIZE);
        }
      }
    }

    // Draw overlay template (transparent guide) on top, only in overlayBounds
    if (overlayVisible && overlayPixels && overlayBounds) {
      const { x1, y1, x2, y2 } = overlayBounds;
      const prevAlpha = ctx.globalAlpha;
      ctx.globalAlpha = overlayOpacity;
      for (let y = y1; y <= y2; y++) {
        for (let x = x1; x <= x2; x++) {
          const color = overlayPixels[y - y1]?.[x - x1];
          if (color) {
            ctx.fillStyle = color;
            ctx.fillRect(x * PIXEL_SIZE, y * PIXEL_SIZE, PIXEL_SIZE, PIXEL_SIZE);
          }
        }
      }
      ctx.globalAlpha = prevAlpha;
    }
  }, [height, pixels, overlayPixels, overlayBounds, overlayVisible, overlayOpacity, width]);

  useEffect(() => {
    renderCanvas();
  }, [renderCanvas]);

  // Get pixel coordinates from client coordinates
  const getPixelCoordsFromClient = useCallback(
    (clientX: number, clientY: number) => {
      const container = containerRef.current;
      if (!container) return null;

      const rect = container.getBoundingClientRect();
      const containerCenterX = rect.width / 2;
      const containerCenterY = rect.height / 2;

      const mouseX = clientX - rect.left;
      const mouseY = clientY - rect.top;

      const canvasX = (mouseX - containerCenterX - panX) / zoom + width / 2;
      const canvasY = (mouseY - containerCenterY - panY) / zoom + height / 2;

      const x = Math.floor(canvasX / PIXEL_SIZE);
      const y = Math.floor(canvasY / PIXEL_SIZE);

      if (x >= 0 && x < width && y >= 0 && y < height) {
        return { x, y };
      }
      return null;
    },
    [height, panX, panY, width, zoom]
  );

  // Get pixel coordinates from mouse event
  const getPixelCoords = useCallback(
    (e: React.MouseEvent | MouseEvent) => {
      return getPixelCoordsFromClient(e.clientX, e.clientY);
    },
    [getPixelCoordsFromClient]
  );

  const handleInspectClick = useCallback(
    (coords: CanvasPixel) => {
      if (inspectedPixel && inspectedPixel.x === coords.x && inspectedPixel.y === coords.y) {
        setInspectedPixel(null);
      } else {
        setInspectedPixel(coords);
      }
      setHoveredPixel(coords);
    },
    [inspectedPixel, setHoveredPixel, setInspectedPixel]
  );

  function handlePlacePixel(targetPixel: CanvasPixel, color: PaletteColor) {
    // Check if user is logged in
    const activeUser = useAuthStore.getState().user;
    if (!activeUser) {
      navigate('/login');
      return;
    }

    // Check timeout (cannot paint while timed out)
    const timeoutUntil = activeUser.timeoutUntil ? new Date(activeUser.timeoutUntil) : null;
    if (timeoutUntil && timeoutUntil > new Date()) {
      showSnackbar('You are timed out and cannot paint. Try again later.', 'warning');
      return;
    }

    // Optimistic update
    setPixel(targetPixel.x, targetPixel.y, color, {
      username: activeUser.username,
      usernameColor: activeUser.usernameColor ?? null,
      usernameEffect: activeUser.usernameEffect ?? null,
      placedAt: new Date().toISOString(),
      userId: activeUser.id,
    });

    // Send to server with YOLO flag
    placePixel(targetPixel.x, targetPixel.y, color, yoloMode);
  }

  // Handle mouse down - start potential drag
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return; // Only left click

      isDragging.current = true;
      hasDragged.current = false;
      dragStartPos.current = { x: e.clientX, y: e.clientY };
      dragStartPan.current = { x: panX, y: panY };
    },
    [panX, panY]
  );

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      // Update hovered pixel
      const coords = getPixelCoords(e);
      setHoveredPixel(coords);

      // Handle dragging
      if (isDragging.current) {
        const deltaX = e.clientX - dragStartPos.current.x;
        const deltaY = e.clientY - dragStartPos.current.y;

        // Check if we've moved enough to be considered a drag
        if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
          hasDragged.current = true;
        }

        if (hasDragged.current) {
          setPan(dragStartPan.current.x + deltaX, dragStartPan.current.y + deltaY);
        }
      }
    },
    [getPixelCoords, setHoveredPixel, setPan]
  );

  // Handle mouse up - either open menu or finish drag
  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging.current) return;

      isDragging.current = false;

      // If we didn't drag, this is a click interaction
      if (!hasDragged.current) {
        const coords = getPixelCoords(e);
        if (coords) {
          if (interactionMode === 'inspect') {
            handleInspectClick(coords);
          } else {
            handlePlacePixel(coords, selectedColor);
          }
        }
      }

      hasDragged.current = false;
    },
    [getPixelCoords, handleInspectClick, handlePlacePixel, interactionMode, selectedColor]
  );

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    isDragging.current = false;
    hasDragged.current = false;
    setHoveredPixel(null);
  }, [setHoveredPixel]);

  // Get distance between two touch points
  const getTouchDistance = (touches: React.TouchList) => {
    const t0 = touches[0];
    const t1 = touches[1];
    if (!t0 || !t1) return null;
    const dx = t0.clientX - t1.clientX;
    const dy = t0.clientY - t1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Get center point between two touches (relative to container center)
  const getTouchCenter = (touches: React.TouchList) => {
    const t0 = touches[0];
    const t1 = touches[1];
    const container = containerRef.current;
    if (!t0 || !t1 || !container) return null;
    const rect = container.getBoundingClientRect();
    return {
      x: (t0.clientX + t1.clientX) / 2 - rect.left - rect.width / 2,
      y: (t0.clientY + t1.clientY) / 2 - rect.top - rect.height / 2,
    };
  };

  // Handle touch start
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        // Single finger - start drag
        const touch = e.touches[0];
        if (!touch) return;
        isDragging.current = true;
        hasDragged.current = false;
        dragStartPos.current = { x: touch.clientX, y: touch.clientY };
        dragStartPan.current = { x: panX, y: panY };
      } else if (e.touches.length === 2) {
        // Two fingers - start pinch zoom
        isDragging.current = false;
        lastPinchDistance.current = getTouchDistance(e.touches);
        lastPinchZoom.current = zoom;
        const center = getTouchCenter(e.touches);
        if (center) {
          pinchCenter.current = center;
          pinchStartPan.current = { x: panX, y: panY };
        }
      }
    },
    [panX, panY, zoom]
  );

  // Handle touch move
  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault(); // Prevent scroll

      if (e.touches.length === 1 && isDragging.current) {
        // Single finger drag
        const touch = e.touches[0];
        if (!touch) return;
        const deltaX = touch.clientX - dragStartPos.current.x;
        const deltaY = touch.clientY - dragStartPos.current.y;

        if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
          hasDragged.current = true;
        }

        if (hasDragged.current) {
          setPan(dragStartPan.current.x + deltaX, dragStartPan.current.y + deltaY);
        }
      } else if (e.touches.length === 2 && lastPinchDistance.current !== null) {
        // Pinch zoom towards center point between fingers
        const newDistance = getTouchDistance(e.touches);
        if (newDistance !== null) {
          const scale = newDistance / lastPinchDistance.current;
          const newZoom = lastPinchZoom.current * scale;

          // Calculate canvas point under pinch center (at start of pinch)
          const canvasPointX =
            (pinchCenter.current.x - pinchStartPan.current.x) / lastPinchZoom.current;
          const canvasPointY =
            (pinchCenter.current.y - pinchStartPan.current.y) / lastPinchZoom.current;

          // Calculate new pan to keep the same canvas point under pinch center
          const newPanX = pinchCenter.current.x - canvasPointX * newZoom;
          const newPanY = pinchCenter.current.y - canvasPointY * newZoom;

          // Use atomic zoom+pan to avoid intermediate clamping issues
          setZoomAndPan(newZoom, newPanX, newPanY);
        }
      }
    },
    [setZoomAndPan]
  );

  // Handle touch end
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 0) {
        // All fingers lifted
        if (!hasDragged.current && isDragging.current) {
          // This was a tap interaction
          const touch = e.changedTouches[0];
          if (touch) {
            const coords = getPixelCoordsFromClient(touch.clientX, touch.clientY);
            if (coords) {
              if (interactionMode === 'inspect') {
                handleInspectClick(coords);
              } else {
                handlePlacePixel(coords, selectedColor);
              }
            }
          }
        }
        isDragging.current = false;
        hasDragged.current = false;
        lastPinchDistance.current = null;
      } else if (e.touches.length === 1) {
        // One finger still down - switch back to drag mode
        const touch = e.touches[0];
        if (touch) {
          isDragging.current = true;
          hasDragged.current = true; // Already moved, so consider it a drag
          dragStartPos.current = { x: touch.clientX, y: touch.clientY };
          dragStartPan.current = { x: panX, y: panY };
          lastPinchDistance.current = null;
        }
      }
    },
    [
      getPixelCoordsFromClient,
      handleInspectClick,
      handlePlacePixel,
      interactionMode,
      panX,
      panY,
      selectedColor,
    ]
  );

  // Wheel zoom: use native listener with passive: false so preventDefault is allowed
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const rect = container.getBoundingClientRect();
      const { cursorX, cursorY } = getCursorPositionFromWheelEvent(rect, e.clientX, e.clientY);
      const delta = getWheelZoomDelta(e.deltaY);
      const newZoom = zoom * delta;

      setZoomAtPoint(newZoom, cursorX, cursorY);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [zoom, setZoomAtPoint]);

  // Arrow key panning when canvas is focused
  useEffect(() => {
    if (!canvasFocused) return;

    const PAN_STEP = 50;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;

      e.preventDefault();

      switch (e.key) {
        case 'ArrowUp':
          setPan(panX, panY + PAN_STEP);
          break;
        case 'ArrowDown':
          setPan(panX, panY - PAN_STEP);
          break;
        case 'ArrowLeft':
          setPan(panX + PAN_STEP, panY);
          break;
        case 'ArrowRight':
          setPan(panX - PAN_STEP, panY);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canvasFocused, panX, panY, setPan]);

  // Clear inspected pixel on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (interactionMode !== 'inspect') return;
      setInspectedPixel(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [interactionMode]);

  // Keyboard shortcuts for mode switching
  useEffect(() => {
    const isTypingTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;

      const key = e.key.toLowerCase();
      if (key === 'i' || key === '1') {
        e.preventDefault();
        setInspectMode();
        return;
      }
      if (key === 'p' || key === '2') {
        e.preventDefault();
        setPaintMode();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setInspectMode, setPaintMode]);

  useEffect(() => {
    if (!expansionCelebration) return;

    const timeout = setTimeout(() => {
      clearExpansionCelebration();
    }, 3600);

    return () => clearTimeout(timeout);
  }, [clearExpansionCelebration, expansionCelebration]);

  // Prevent context menu
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const canvasStyle = {
    transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
    imageRendering: 'pixelated' as const,
  };
  const completionPercent = Math.min(100, Math.round(completionRatio * 1000) / 10);
  const thresholdPercent = Math.round(completionThreshold * 100);
  const progressToExpansion =
    completionThreshold > 0 ? Math.min(100, (completionRatio / completionThreshold) * 100) : 0;

  const scrollPaletteBy = useCallback((delta: number) => {
    const mobileEl = paletteScrollMobileRef.current;
    const desktopEl = paletteScrollDesktopRef.current;
    const el = mobileEl && mobileEl.clientWidth > 0 ? mobileEl : desktopEl;
    if (!el) return;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  }, []);

  const renderPaintPalette = (scrollRef: { current: HTMLDivElement | null }) => (
    <div className="bg-wall-surface/85 backdrop-blur-xs border border-wall-border/50 rounded-xl shadow-lg px-3 py-1.5 h-[78.5px] w-full min-[1041px]:w-[min(92vw,760px)] overflow-hidden">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-wall-muted">Selected color</span>
        <div className="flex items-center gap-2">
          <div
            className="h-4 w-4 rounded-sm border border-wall-border/60"
            style={{ backgroundColor: selectedColor }}
          />
          <span className="text-xs font-mono text-wall-text">{selectedColor}</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => scrollPaletteBy(-160)}
          className="shrink-0 h-8 w-8 rounded-md border border-wall-border/60 bg-wall-bg/60 text-wall-muted hover:text-wall-text hover:bg-wall-border/40 transition-colors flex items-center justify-center"
          aria-label="Scroll colors left"
          title="Scroll left"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 18l-6-6 6-6"
            />
          </svg>
        </button>

        <div
          ref={scrollRef}
          className="palette-scrollbar flex-1 overflow-x-scroll overflow-y-hidden pb-1"
        >
          <div className="flex min-w-max items-center gap-2">
            {COLOR_PALETTE.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => setSelectedColor(color as PaletteColor)}
                className={`shrink-0 w-8 h-8 rounded-md border transition-all ${
                  selectedColor === color
                    ? 'border-white ring-2 ring-inset ring-wall-accent'
                    : 'border-wall-border/60 hover:scale-105'
                }`}
                style={{ backgroundColor: color }}
                title={color}
                aria-label={`Select color ${color}`}
              />
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => scrollPaletteBy(160)}
          className="shrink-0 h-8 w-8 rounded-md border border-wall-border/60 bg-wall-bg/60 text-wall-muted hover:text-wall-text hover:bg-wall-border/40 transition-colors flex items-center justify-center"
          aria-label="Scroll colors right"
          title="Scroll right"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 6l6 6-6 6" />
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <SEO
        title="Live Canvas"
        description="Paint pixels on a shared canvas with the community. The Wall expands as it fills, and finished canvases are preserved in the gallery."
        url="/"
      />
      <div className="relative h-full flex">
        {/* Canvas Container */}
        <div
          ref={containerRef}
          tabIndex={0}
          className={`flex-1 overflow-hidden bg-wall-bg flex items-center justify-center select-none outline-hidden ${
            isDragging.current && hasDragged.current ? 'cursor-grabbing' : 'cursor-crosshair'
          } ${canvasFocused ? 'ring-2 ring-wall-accent/30 ring-inset' : ''}`}
          style={{
            touchAction: 'none',
            overscrollBehavior: 'contain',
          }}
          onMouseDown={(e) => {
            containerRef.current?.focus();
            handleMouseDown(e);
          }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onFocus={() => setCanvasFocused(true)}
          onBlur={() => setCanvasFocused(false)}
          onTouchStart={(e) => {
            containerRef.current?.focus();
            handleTouchStart(e);
          }}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onContextMenu={handleContextMenu}
        >
          <canvas
            ref={canvasRef}
            width={width * PIXEL_SIZE}
            height={height * PIXEL_SIZE}
            style={canvasStyle}
            className="border border-wall-border"
          />

          {/* Hover indicator */}
          {hoveredPixel && zoom >= 4 && (
            <div
              className="absolute pointer-events-none border-2 border-white mix-blend-difference"
              style={{
                width: PIXEL_SIZE * zoom,
                height: PIXEL_SIZE * zoom,
                left: `calc(50% + ${panX}px + ${(hoveredPixel.x - width / 2) * PIXEL_SIZE * zoom}px)`,
                top: `calc(50% + ${panY}px + ${(hoveredPixel.y - height / 2) * PIXEL_SIZE * zoom}px)`,
              }}
            />
          )}

          {/* Selected pixel highlight */}
          {inspectedPixel && interactionMode === 'inspect' && (
            <div
              className="absolute pointer-events-none border-2 border-wall-accent animate-pulse"
              style={{
                width: PIXEL_SIZE * zoom,
                height: PIXEL_SIZE * zoom,
                left: `calc(50% + ${panX}px + ${(inspectedPixel.x - width / 2) * PIXEL_SIZE * zoom}px)`,
                top: `calc(50% + ${panY}px + ${(inspectedPixel.y - height / 2) * PIXEL_SIZE * zoom}px)`,
              }}
            />
          )}
        </div>

        {/* Floating UI - Top Left */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {isConnected && isAdmin ? (
            <button
              type="button"
              onClick={() => setLiveModalOpen(true)}
              className="w-32 px-3 py-1.5 rounded-lg text-sm font-medium backdrop-blur-xs flex items-center gap-2 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30 transition-colors cursor-pointer"
              aria-label="View who is live on the canvas"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Live
            </button>
          ) : (
            <div
              className={`w-32 px-3 py-1.5 rounded-lg text-sm font-medium backdrop-blur-xs flex items-center gap-2 ${
                !user
                  ? 'bg-wall-surface/80 text-wall-muted border border-wall-border/50'
                  : isConnected
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-red-500/20 text-red-400 border border-red-500/30'
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  !user ? 'bg-wall-muted' : isConnected ? 'bg-emerald-400' : 'bg-red-400'
                } ${user ? 'animate-pulse' : ''}`}
              />
              {!user ? 'Viewing' : isConnected ? 'Live' : 'Offline'}
            </div>
          )}
          <div className="px-3 py-1.5 bg-wall-surface/80 backdrop-blur-xs border border-wall-border/50 rounded-lg text-sm text-wall-muted hidden sm:block">
            <span>
              {interactionMode === 'paint'
                ? 'Paint mode: click to place • Drag to pan'
                : 'Inspect mode: click to inspect • Drag to pan'}
            </span>
          </div>
          <button
            type="button"
            onClick={openWelcomeModal}
            className="px-3 py-1.5 bg-wall-surface/80 backdrop-blur-xs border border-wall-border/50 rounded-lg text-sm text-wall-muted hover:text-wall-text transition-colors text-left w-fit"
            aria-label="How it works"
          >
            How it works
          </button>
        </div>

        {/* Right sidebar */}
        <div className="absolute right-4 top-4 flex flex-col gap-4">
          <ZoomControls />
          <OverlayPanel />
          <div className="bg-wall-surface/80 backdrop-blur-xs border border-wall-border/50 rounded-xl px-4 py-3 shadow-lg min-w-[160px]">
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="text-xs text-wall-muted uppercase tracking-wide font-medium">
                Expansion
              </span>
              <span className="text-sm font-bold text-wall-accent">{completionPercent}%</span>
            </div>
            <div className="h-2 bg-wall-bg rounded-full overflow-hidden">
              <div
                className="h-full bg-linear-to-r from-wall-accent to-wall-accent-secondary transition-all duration-300 rounded-full"
                style={{ width: `${progressToExpansion}%` }}
              />
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-wall-muted">
              <span>
                {activePixels.toLocaleString()}/{totalPixels.toLocaleString()}
              </span>
              <span className="text-right">{thresholdPercent}% target</span>
              <span>
                {width}x{height}
              </span>
              <span className="text-right">+{expandByPixels}px sides</span>
              {expansionCount > 0 && (
                <button
                  type="button"
                  onClick={() => setExpansionModalOpen(true)}
                  className="col-span-2 text-left text-wall-accent hover:underline cursor-pointer"
                  title="View expansion history"
                >
                  {expansionCount} permanent expansion{expansionCount === 1 ? '' : 's'}
                </button>
              )}
            </div>
          </div>
          <AdminPanel />
        </div>

        {/* Bottom compact menu (<1041px): top controls + bottom active panel */}
        <div className="min-[1041px]:hidden absolute bottom-[14px] left-4 right-4 z-40 pointer-events-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={setInspectMode}
                className={`h-10 w-10 flex items-center justify-center rounded-xl border backdrop-blur-xs shadow-lg transition-colors ${
                  interactionMode === 'inspect'
                    ? 'bg-wall-accent text-white border-wall-accent'
                    : 'bg-wall-surface/80 text-wall-muted border-wall-border/50 hover:text-wall-text hover:bg-wall-border/40'
                }`}
                title="Inspect mode (I or 1)"
                aria-label="Switch to inspect mode"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M10.611 10.611a1 1 0 0 1 1.11-.208l8.839 3.889a1 1 0 0 1-.14 1.88l-3.338.91l-.91 3.338a1 1 0 0 1-1.88.14l-3.89-8.84a1 1 0 0 1 .209-1.109M17 3a3 3 0 0 1 3 3v3a1 1 0 1 1-2 0V6a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h3a1 1 0 1 1 0 2H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3zm-3.73 10.269l1.715 3.9l.318-1.164a1 1 0 0 1 .701-.702l1.165-.318l-3.9-1.716Z"
                  />
                </svg>
              </button>

              <button
                type="button"
                onClick={setPaintMode}
                className={`h-10 w-10 flex items-center justify-center rounded-xl border backdrop-blur-xs shadow-lg transition-colors ${
                  interactionMode === 'paint'
                    ? 'bg-wall-accent text-white border-wall-accent'
                    : 'bg-wall-surface/80 text-wall-muted border-wall-border/50 hover:text-wall-text hover:bg-wall-border/40'
                }`}
                title="Paint mode (P or 2)"
                aria-label="Switch to paint mode"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4a8 8 0 00-8 8v1a3 3 0 003 3h2l5 4 1.5-3H18a3 3 0 003-3v-2a8 8 0 00-8-8z"
                  />
                  <circle cx="9" cy="11" r="1.1" fill="currentColor" stroke="none" />
                  <circle cx="12" cy="9.5" r="1.1" fill="currentColor" stroke="none" />
                  <circle cx="15" cy="11.5" r="1.1" fill="currentColor" stroke="none" />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a3 3 0 003 3h8"
                  />
                </svg>
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => openLeaderboard(true)}
                className="w-11 h-11 rounded-full flex items-center justify-center touch-manipulation bg-wall-surface/90 backdrop-blur-md border border-wall-border/60 text-wall-muted hover:text-wall-accent hover:border-wall-accent/40 hover:bg-wall-surface shadow-lg shadow-black/20 transition-all duration-200 hover:scale-105 active:scale-95"
                aria-label="Open leaderboard"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </button>

              <button
                type="button"
                onClick={() => openChat(true)}
                className="w-11 h-11 rounded-full flex items-center justify-center touch-manipulation relative bg-wall-surface/90 backdrop-blur-md border border-wall-accent/50 text-wall-accent shadow-lg shadow-black/20 hover:border-wall-accent hover:shadow-glow hover:bg-wall-surface/95 transition-all duration-200 hover:scale-105 active:scale-95"
                aria-label={hasUnreadChat ? 'Open chat (new messages)' : 'Open chat'}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                {hasUnreadChat && (
                  <span
                    className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-wall-accent shadow-[0_0_0_2px_var(--color-wall-surface)] animate-pulse"
                    aria-hidden
                  />
                )}
              </button>
            </div>
          </div>

          <div className="mt-2">
            {interactionMode === 'paint' ? (
              renderPaintPalette(paletteScrollMobileRef)
            ) : (
              <PixelInfo
                pixel={inspectedPixel}
                idleLabel="Hover over the canvas"
                followHover
                statusText={inspectedPixel ? 'Selected pixel' : undefined}
              />
            )}
          </div>
        </div>

        {/* Bottom left: modes (desktop >=1041px) */}
        <div className="max-[1040px]:hidden absolute bottom-[22px] left-4 z-30 flex flex-col items-start gap-2 pointer-events-auto p-2 -m-2">
          <button
            type="button"
            onClick={setInspectMode}
            className={`h-10 w-10 sm:w-fit sm:px-3 flex items-center justify-center gap-2 rounded-xl border backdrop-blur-xs pointer-events-auto shadow-lg transition-colors ${
              interactionMode === 'inspect'
                ? 'bg-wall-accent text-white border-wall-accent'
                : 'bg-wall-surface/80 text-wall-muted border-wall-border/50 hover:text-wall-text hover:bg-wall-border/40'
            }`}
            title="Inspect mode (I or 1)"
            aria-label="Switch to inspect mode"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M10.611 10.611a1 1 0 0 1 1.11-.208l8.839 3.889a1 1 0 0 1-.14 1.88l-3.338.91l-.91 3.338a1 1 0 0 1-1.88.14l-3.89-8.84a1 1 0 0 1 .209-1.109M17 3a3 3 0 0 1 3 3v3a1 1 0 1 1-2 0V6a1 1 0 0 0-1-1H6a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h3a1 1 0 1 1 0 2H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3zm-3.73 10.269l1.715 3.9l.318-1.164a1 1 0 0 1 .701-.702l1.165-.318l-3.9-1.716Z"
              />
            </svg>
            <span className="hidden sm:inline text-xs font-semibold leading-none">Inspect</span>
          </button>

          <button
            type="button"
            onClick={setPaintMode}
            className={`h-10 w-10 sm:w-fit sm:px-3 flex items-center justify-center gap-2 rounded-xl border backdrop-blur-xs pointer-events-auto shadow-lg transition-colors ${
              interactionMode === 'paint'
                ? 'bg-wall-accent text-white border-wall-accent'
                : 'bg-wall-surface/80 text-wall-muted border-wall-border/50 hover:text-wall-text hover:bg-wall-border/40'
            }`}
            title="Paint mode (P or 2)"
            aria-label="Switch to paint mode"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4a8 8 0 00-8 8v1a3 3 0 003 3h2l5 4 1.5-3H18a3 3 0 003-3v-2a8 8 0 00-8-8z"
              />
              <circle cx="9" cy="11" r="1.1" fill="currentColor" stroke="none" />
              <circle cx="12" cy="9.5" r="1.1" fill="currentColor" stroke="none" />
              <circle cx="15" cy="11.5" r="1.1" fill="currentColor" stroke="none" />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a3 3 0 003 3h8"
              />
            </svg>
            <span className="hidden sm:inline text-xs font-semibold leading-none">Paint</span>
          </button>
        </div>

        {/* Bottom center: inspect info (desktop >=1041px) */}
        {interactionMode === 'inspect' && (
          <div className="max-[1040px]:hidden absolute bottom-5.5 left-1/2 -translate-x-1/2 z-40 pointer-events-auto">
            <PixelInfo
              pixel={inspectedPixel}
              idleLabel="Hover over the canvas"
              followHover
              statusText={inspectedPixel ? 'Selected pixel' : undefined}
            />
          </div>
        )}

        {/* Bottom center: paint palette (desktop >=1041px) */}
        {interactionMode === 'paint' && (
          <div className="max-[1040px]:hidden block absolute bottom-4 left-1/2 -translate-x-1/2 z-20 pointer-events-auto">
            {renderPaintPalette(paletteScrollDesktopRef)}
          </div>
        )}

        {expansionCelebration && (
          <div
            className="canvas-expansion-celebration pointer-events-none absolute inset-0 z-50 flex items-center justify-center overflow-hidden"
            aria-live="polite"
            aria-atomic="true"
          >
            <div className="canvas-expansion-ring canvas-expansion-ring-a" />
            <div className="canvas-expansion-ring canvas-expansion-ring-b" />
            <div className="canvas-expansion-particles" aria-hidden>
              {Array.from({ length: 36 }, (_, index) => (
                <span
                  key={`${expansionCelebration.id}-${index}`}
                  className="canvas-expansion-particle"
                  style={
                    {
                      '--particle-index': index,
                      '--particle-angle': `${index * 10}deg`,
                      '--particle-distance': `${120 + (index % 6) * 28}px`,
                      '--particle-color': COLOR_PALETTE[index % COLOR_PALETTE.length],
                    } as CSSProperties
                  }
                />
              ))}
            </div>
            <div className="canvas-expansion-copy">
              <h2>Canvas Explosion!</h2>
              <p>The canvas has been expanded permanently</p>
              <span>
                +{expansionCelebration.expandedByPixels}px every side - {expansionCelebration.width}
                x{expansionCelebration.height}
              </span>
            </div>
          </div>
        )}

        <LiveUsersModal isOpen={liveModalOpen} onClose={() => setLiveModalOpen(false)} />
        <ExpansionHistoryModal
          isOpen={expansionModalOpen}
          onClose={() => setExpansionModalOpen(false)}
        />
      </div>
    </>
  );
}
